import {registerTourSteps} from './product-tours.js'

// Walk-through of the ?v=4 patient chart: the sidebar's three states (open, folded rail, hidden
// "focus mode"), the two sidebar panels, and the keyboard shortcuts. Launched from the tours
// drawer through the shared registry launcher in product-tours.js.
export const PATIENT_CHART_V4_TOUR_ID = 'patient-chart-v4-layout'

const rootSelector = '[data-chart-v4-root]'
const wait = ms => new Promise(resolve => setTimeout(resolve, ms))

export function patientChartV4TourSteps(documentObject, browserWindow) {
    const root = documentObject.querySelector(rootSelector)
    const chart = root && browserWindow.Alpine ? browserWindow.Alpine.$data(root) : null

    if (!chart) {
        return {
            steps: [{
                selector: null,
                title: 'Open a patient chart first',
                text: 'Open any patient chart in the new layout (add ?v=4 to its address), then start this tour again from the tours list.',
            }],
            onEnd: null,
        }
    }

    const initial = {collapsed: chart.collapsed, hiddenSidebar: chart.hiddenSidebar, panel: chart.panel}
    const shortcut = chart.shortcutLabel ?? 'Ctrl+'

    const openSidebar = async (panel = 'patient') => {
        if (!chart.desktop) return
        if (chart.hiddenSidebar) chart.showSidebar()
        chart.collapsed = false
        chart.panel = panel
        await wait(300)
    }

    // On a phone the sidebar buttons are not on screen, so the click steps become plain explanations.
    const click = selector => chart.desktop ? {selector, event: 'click'} : undefined

    const steps = [
        {
            selector: null,
            title: 'Welcome to the new chart layout',
            text: 'Everything you used before is still here, in a tidier place. This short tour shows where things are, the three sidebar sizes, and the keyboard shortcuts.',
        },
        {
            selector: '[data-cy="header-patient-name"]',
            position: 'right',
            before: () => openSidebar('patient'),
            title: 'Who you are working on',
            text: 'The patient’s name, date of birth, MRN and ID sit at the top of the sidebar. Use the copy icon next to each to grab it for an EHR lookup.',
        },
        {
            selector: '[data-cy="chart-v4-nurse-mode"]',
            position: 'bottom',
            before: () => openSidebar('patient'),
            advanceOn: click('[data-cy="chart-v4-nurse-mode"]'),
            title: 'My tools',
            text: 'The sidebar has two panels. You are on the patient panel. Click the briefcase to open your own tools.',
        },
        {
            selector: '[data-cy="chart-v4-nurse-panel"]',
            position: 'right',
            before: () => openSidebar('nurse'),
            title: 'Your pages, in one place',
            text: 'Home, work schedule, invoice / pay, activity history and more are here, with the magnifier to find another patient and the tours list.',
        },
        {
            selector: '[data-cy="chart-v4-patient-mode"]',
            position: 'bottom',
            advanceOn: click('[data-cy="chart-v4-patient-mode"]'),
            title: 'Back to the patient',
            text: 'Click the person icon to return to this patient.',
        },
        {
            selector: '[data-cy="chart-v4-call-actions"]',
            position: 'right',
            before: () => openSidebar('patient'),
            title: 'Call and New note',
            text: 'Your timer, Call and New note buttons stay in the sidebar, so they are always one click away.',
        },
        {
            selector: '[data-cy="patient-details"]',
            position: 'right',
            before: () => openSidebar('patient'),
            title: 'Patient details',
            text: 'Phone, doctors, practice and family live here. Click any section title to fold it; the sidebar remembers what you left open.',
        },
        {
            selector: '[data-cy="call-contact-section"]',
            position: 'right',
            before: () => openSidebar('patient'),
            title: 'Calls & contact',
            text: 'The next call, the count of consecutive unsuccessful calls, and the patient’s contact preferences.',
        },
        {
            selector: '[data-cy="chronic-conditions-section"]',
            position: 'right',
            before: () => openSidebar('patient'),
            title: 'Chronic conditions',
            text: 'Always in view, so you can check them before every call.',
        },
        {
            selector: '[data-cy="chart-tabs"]',
            position: 'bottom',
            before: () => openSidebar('patient'),
            title: 'Chart tabs',
            text: 'Overview, Activities, Care Plan, Time Report (admins) and Profile sit in a strip above the chart, in every sidebar size. Patient actions, such as Add care person, are in the sidebar.',
        },
        {
            selector: '[data-cy="chart-v4-account-menu"]',
            position: 'right',
            before: () => openSidebar('patient'),
            advanceOn: click('[data-cy="chart-v4-account-menu"]'),
            title: 'Your account',
            text: 'The CarePlanManager logo opens your account menu. Click it now.',
        },
        {
            selector: '[data-cy="chart-v4-account-menu"]',
            position: 'right',
            title: 'Your account menu',
            text: 'Your hours and pay, activity history, notification preferences, account settings and Log Out are here.',
        },
        {
            selector: '[data-cy="chart-v4-bubble"]',
            position: 'right',
            before: async () => {
                // The account menu closes on a click outside it; close it so it does not cover the buttons next to it.
                documentObject.body.click()
                await wait(200)
            },
            title: 'Messenger',
            text: 'Messages from your team and from support. The number shows how many are unread.',
        },
        {
            selector: '[data-cy="chart-v4-bug-report"]',
            position: 'right',
            title: 'Report a bug',
            text: 'Something not working? Use this button to tell the team what happened.',
        },
        {
            selector: '[data-cy="chart-sidebar-toggle"]',
            position: 'right',
            before: () => openSidebar('patient'),
            title: 'Sidebar size 1 of 3: open',
            text: 'This is the full sidebar, with everything about the patient in view.',
        },
        {
            selector: '[data-cy="chart-v4-sidebar-resize"]',
            position: 'right',
            before: () => openSidebar('patient'),
            waitFor: chart.desktop ? done => {
                const startWidth = chart.sidebarWidth
                // Pointer-up fires after the page's own resize handler has set the width, so compare then.
                const onRelease = () => browserWindow.setTimeout(() => {
                    if (Math.abs(chart.sidebarWidth - startWidth) >= 8) done()
                }, 50)

                browserWindow.addEventListener('pointerup', onRelease)

                return () => browserWindow.removeEventListener('pointerup', onRelease)
            } : undefined,
            title: 'Make it wider or narrower',
            text: 'Drag the sidebar’s right edge to the left or right to resize it. Try it now.',
        },
        {
            selector: '[data-cy="chart-sidebar-toggle"]',
            position: 'right',
            before: () => openSidebar('patient'),
            advanceOn: click('[data-cy="chart-sidebar-toggle"]'),
            title: 'Sidebar size 2 of 3: folded',
            text: 'Click the sidebar button now to fold it into a slim icon rail. You can also press ' + shortcut + 'B.',
        },
        {
            selector: '[data-cy="chart-sidebar-toggle"]',
            position: 'right',
            advanceOn: click('[data-cy="chart-sidebar-toggle"]'),
            title: 'Open it again',
            text: 'Click the same button again to open the sidebar.',
        },
        {
            selector: '[data-cy="chart-v4-show-sidebar"]',
            position: 'bottom',
            before: async () => {
                if (!chart.desktop) return
                chart.hideSidebar()
                await wait(350)
            },
            advanceOn: click('[data-cy="chart-v4-show-sidebar"]'),
            title: 'Sidebar size 3 of 3: focus mode',
            text: 'Double-click the sidebar button, or press ' + shortcut + '\\, to hide the sidebar completely. In this view you only see the buttons you may need during a call and while charting with a patient: the patient, timer, Call and New note sit in a slim bar at the top. The sidebar is hidden now: click this button to bring it back.',
        },
        {
            selector: null,
            before: async () => {
                if (!chart.desktop) return
                chart.showSidebar()
                await wait(300)
            },
            title: 'That’s it',
            text: 'Shortcuts: ' + shortcut + 'B opens or folds the sidebar, ' + shortcut + '\\ turns focus mode on or off. You can replay this tour from the tours list in My tools.',
        },
    ]

    return {
        steps,
        onEnd: () => {
            if (!chart.desktop) return
            chart.hiddenSidebar = initial.hiddenSidebar
            chart.collapsed = initial.collapsed
            chart.panel = initial.panel
        },
    }
}

export function registerPatientChartV4Tour(browserWindow = window, documentObject = document) {
    registerTourSteps(PATIENT_CHART_V4_TOUR_ID, patientChartV4TourSteps)

    // The server marks the chart root `data-chart-v4-auto-tour` until the user has any progress on this tour,
    // so it opens once, the first time someone sees v4.
    let startedRoot = null
    let attempts = 0
    const startIfDue = () => {
        const root = documentObject.querySelector('[data-chart-v4-auto-tour]')

        if (!root || root === startedRoot) return

        startedRoot = root
        browserWindow.setTimeout(() => {
            if (documentObject.querySelector('[data-chart-v4-auto-tour]') !== root) {
                startedRoot = null
                return
            }
            if (!browserWindow.Alpine?.$data(root)) {
                startedRoot = null
                if (++attempts < 12) browserWindow.setTimeout(startIfDue, 250)
                return
            }
            browserWindow.cpmStartTour?.(PATIENT_CHART_V4_TOUR_ID)
        }, 900)
    }

    documentObject.addEventListener('livewire:navigated', startIfDue)
    documentObject.addEventListener('DOMContentLoaded', startIfDue)
    documentObject.addEventListener('alpine:initialized', startIfDue)
    browserWindow.addEventListener('load', startIfDue)
    startIfDue()
}
